import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Heart, FileText, Download } from "lucide-react";
import axios from "axios";
import { bonusDownloads } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 10;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);

  useEffect(() => {
    let attempts = 0;
    let timer;

    const poll = async () => {
      if (!sessionId) {
        setState("error");
        return;
      }
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/payments/status/${sessionId}`
        );
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        // keep trying
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };

    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell>
      {state === "checking" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-[#6618D0]" />
          <h1 className="mt-6 font-serif-display text-3xl font-bold text-[#6618D0]">
            Confirming your order…
          </h1>
          <p className="mt-3 text-[15px] text-[#5a5a5a]">
            Hang tight while we verify your payment.
          </p>
        </>
      )}

      {state === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-[#48B6F7]" />
          <h1 className="mt-6 font-serif-display text-3xl font-bold text-[#6618D0]">
            Thank you{order?.customer_name ? `, ${order.customer_name.split(" ")[0]}` : ""}!
          </h1>
          <p className="mt-3 max-w-md text-[15px] leading-relaxed text-[#5a5a5a]">
            Your presale order for{" "}
            <span className="font-semibold text-[#1E1E1E]">
              {order?.quantity || 1} {order?.quantity > 1 ? "copies" : "copy"}
            </span>{" "}
            of <em>The Mother Behind That Child</em> is confirmed. A receipt is
            on its way to your inbox.
          </p>
          <BonusDownloads />
          <HomeButton />
        </>
      )}

      {state === "pending" && (
        <>
          <Loader2 className="h-12 w-12 text-[#9A6CE3]" />
          <h1 className="mt-6 font-serif-display text-3xl font-bold text-[#6618D0]">
            Payment is processing
          </h1>
          <p className="mt-3 max-w-md text-[15px] text-[#5a5a5a]">
            Your payment is still being confirmed. You'll receive an email once
            it completes. Feel free to check back shortly.
          </p>
          <HomeButton />
        </>
      )}

      {state === "error" && (
        <>
          <XCircle className="h-14 w-14 text-[#990EE5]" />
          <h1 className="mt-6 font-serif-display text-3xl font-bold text-[#6618D0]">
            We couldn't confirm your order
          </h1>
          <p className="mt-3 max-w-md text-[15px] text-[#5a5a5a]">
            Something went wrong verifying your payment. If you were charged,
            please contact us and we'll sort it out right away.
          </p>
          <HomeButton />
        </>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <XCircle className="h-14 w-14 text-[#990EE5]" />
      <h1 className="mt-6 font-serif-display text-3xl font-bold text-[#6618D0]">
        Order cancelled
      </h1>
      <p className="mt-3 max-w-md text-[15px] text-[#5a5a5a]">
        No worries — you haven't been charged. Your copy is still waiting
        whenever you're ready to reserve it.
      </p>
      <HomeButton label="Back to presale" />
    </Shell>
  );
}

function BonusDownloads() {
  return (
    <div data-testid="bonus-downloads" className="mt-8 w-full max-w-md rounded-2xl border border-[#D8CFF0] bg-white p-6 text-left shadow-sm">
      <h2 className="font-serif-display text-lg font-bold text-[#6618D0]">Your presale bonuses</h2>
      <p className="mt-1 text-[13px] text-[#5a5a5a]">Download them now and keep them close for the hard moments.</p>
      <div className="mt-4 space-y-3">
        {bonusDownloads.map((d) => (
          <a
            key={d.id}
            href={d.file}
            download
            data-testid={`download-${d.id}`}
            className="flex items-center gap-3 rounded-xl bg-[#F3EEFC] px-4 py-3 transition-colors hover:bg-[#E3DDF7]"
          >
            <FileText className="h-6 w-6 shrink-0 text-[#990EE5]" />
            <span className="flex-1">
              <span className="block text-[14px] font-semibold leading-snug text-[#2a2a2a]">{d.title}</span>
              <span className="block text-[11px] text-[#7a7a7a]">{d.type}</span>
            </span>
            <Download className="h-5 w-5 shrink-0 text-[#6618D0]" />
          </a>
        ))}
      </div>
      <p className="mt-4 text-[12px] text-[#7a7a7a]">The private launch day livestream invite will arrive by email.</p>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#EAE9F6] px-6 text-center">
      <div className="mb-8 flex items-center gap-2 text-[#6618D0]">
        <Heart className="h-6 w-6 fill-[#990EE5] text-[#990EE5]" />
        <span className="font-serif-display text-lg font-bold">
          The Motivated Parents &amp; Educators
        </span>
      </div>
      {children}
    </div>
  );
}

function HomeButton({ label = "Back to home" }) {
  return (
    <Link
      to="/"
      className="mt-8 rounded-md bg-[#6618D0] px-7 py-3 font-serif-display text-sm font-bold tracking-wide text-white shadow-sm transition-all hover:bg-[#5512b0] active:scale-[0.99]"
    >
      {label}
    </Link>
  );
}
